import { Component, Input, Output, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { IconsDirective } from '@pkg-directives/icons/icons.directive';
import { filter, Subscription } from 'rxjs';

export interface SidebarMenuItem {
  label: string;
  route: string;
  icon: string;
  badge?: number;
  queryParams?: Record<string, string>;
  exact?: boolean;
  /** Adresses où l'entrée est active (par défaut : sa route et ses sous-pages). */
  activeUnder?: string[];
  /** Sous-pages qui appartiennent à une autre entrée (ex. /admin/prospection/campagnes → Campagnes). */
  notUnder?: string[];
}

@Component({
  selector: 'app-sidebar-menu-group',
  standalone: true,
  imports: [CommonModule, RouterModule, IconsDirective],
  template: `
    <div class="menu-group" [class.sidebar-collapsed]="sidebarCollapsed" [class.group-open]="isOpen">
      @if (!sidebarCollapsed) {
        <button
          type="button"
          class="group-header"
          (click)="toggle()"
          [class]="{'isOpenSection': isOpen}"
          [attr.aria-expanded]="isOpen">
          <span class="group-label">{{ groupLabel }}</span>
          <span class="group-chevron" [class.rotated]="isOpen">
            <i name="chevron-bottom" [size]="5" [color]="'rgba(255,255,255,0.55)'" Icons></i>
          </span>
        </button>
      }
      @if (isOpen || sidebarCollapsed) {
        <ul class="group-list">
          @for (item of items; track item.label) {
            <li class="group-item">
              <a
                [routerLink]="item.route"
                [queryParams]="item.queryParams"
                [class.active]="isItemActive(item)"
                class="menu-link"
                [attr.title]="sidebarCollapsed ? item.label : null"
                (click)="itemClick.emit()"
                (mouseenter)="sidebarCollapsed && itemHover.emit({ label: item.label, clientY: $event.clientY })"
                (mouseleave)="sidebarCollapsed && itemLeave.emit()">
                <span class="menu-icon">
                  <i [name]="item.icon" [size]="10" [color]="'#fff'" Icons></i>
                </span>
                @if (!sidebarCollapsed) {
                  <span class="menu-label">{{ item.label }}</span>
                }
                @if (item.badge && item.badge > 0) {
                  <span class="menu-badge" [class.menu-badge--collapsed]="sidebarCollapsed">
                    {{ item.badge > 9 ? '9+' : item.badge }}
                  </span>
                }
              </a>
            </li>
          }
        </ul>
      }
    </div>
  `,
  styleUrl: './sidebar-menu-group.component.scss'
})
export class SidebarMenuGroupComponent {
  @Input() groupLabel = '';
  @Input() items: SidebarMenuItem[] = [];
  @Input() sidebarCollapsed = false;
  @Input() defaultOpen = true;
  @Output() itemClick = new EventEmitter<void>();
  @Output() itemHover = new EventEmitter<{ label: string; clientY: number }>();
  @Output() itemLeave = new EventEmitter<void>();

  isOpen = true;
  private sub?: Subscription;

  constructor(private router: Router) {}

  ngOnInit(): void {
    // À l'arrivée : la rubrique de la page ouverte (et celle ouverte par défaut). Ensuite, en changeant de page,
    // la rubrique de la nouvelle page s'ouvre et les autres se referment ; chacune reste fermable (30/10/2026).
    this.isOpen = this.hasActiveLink() || this.defaultOpen;
    this.sub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => (this.isOpen = this.hasActiveLink()));
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  /**
   * Entrée active : sa route (exacte pour le dashboard et `exact`), sinon l'une de ses adresses `activeUnder`
   * et leurs sous-pages, hors `notUnder`. Une rubrique = une entrée, ses pages sont des onglets (30/10/2026).
   */
  isItemActive(item: SidebarMenuItem): boolean {
    const path = this.router.url.split(/[?#]/)[0];
    const under = (prefix: string) => path === prefix || path.startsWith(prefix + '/');
    if (item.exact || item.route === '/admin/dashboard') return path === item.route;
    if ((item.notUnder ?? []).some(under)) return false;
    if (!(item.activeUnder ?? [item.route]).some(under)) return false;
    // Deux entrées sur la même page (Messages contact / Demandes d'activité) : le filtre qui les distingue
    // doit correspondre ; le statut, lui, change au gré des filtres de la page.
    const query = this.router.parseUrl(this.router.url).queryParams;
    return Object.entries(item.queryParams ?? {}).every(([k, v]) => k === 'status' || query[k] === undefined || query[k] === v);
  }

  private hasActiveLink(): boolean {
    return (this.items || []).some((item) => !!item?.route && this.isItemActive(item));
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
  }
}
